import { get } from 'lodash';

import { ActualContainerItem, ContextualNotesSchemaResourceComplex, DenominationResource, MaterialResource, MeasurementResource, OwnerInstitutionResource, SkinDate, TechniqueResource, TranslatedString, TypedTextResource, Wemi, getTranslatedString } from '@skinsoft/types'
import { itemSelectors, workSelectors, manifestationSelectors, wemiSelectors } from '.'
/**
 * Select work title first and fallback on manifestation title
 *  if first is null or empty
 * @param wemi 
 */
export const title = (wemi: Wemi, locale: string) => {
    return wemi.work && workSelectors.title(wemi.work, locale) ||
        wemi.manifestation && manifestationSelectors.title(wemi.manifestation, locale);
}

// complex fields
export const creationDate = (wemi: Wemi) => {
    return workCreationDate(wemi) ?? itemCreationDate(wemi) ?? "";
}

// item fields
const itemCreationDate = (wemi: Wemi) => wemi.item ? itemSelectors.creationDate(wemi.item) : ""
export const id = (wemi: Wemi) => wemi.item ? itemSelectors.id(wemi.item) : "";
export const itemType = (wemi: Wemi) => wemi.item ? itemSelectors.type(wemi.item) : "";
export const institution = (wemi: Wemi) => wemi.item ? itemSelectors.institution(wemi.item) : "";
export const inventoryNumber = (wemi: Wemi, locale: string) => wemi.item ? itemSelectors.identifier(wemi.item, locale) : "";
export const media = (wemi: Wemi) => wemi.item ? itemSelectors.media(wemi.item) : ""
export const lastDateOfConditionReport = (wemi: Wemi) => wemi.item ? itemSelectors.lastDateOfConditionReport(wemi.item) : "";
export const lastConditionReport = (wemi: Wemi) => wemi.item ? itemSelectors.lastConditionReport(wemi.item) : "";

export const permanentLocation = (wemi: Wemi) => wemi.item ? itemSelectors.permanentLocation(wemi.item) : ""
export const realTimeLocation = (wemi: Wemi): string => {
    return wemi.item ? itemSelectors.realTimeLocation(wemi.item) : ""
}
export const futureLocation = (wemi: Wemi) => wemi.item ? itemSelectors.futureLocation(wemi.item) : ""
export const notesOnIdentification = (wemi: Wemi): ContextualNotesSchemaResourceComplex[] => {
    if(wemi.item) {
        return itemSelectors.notesOnIdentification(wemi.item)
    }
    return []
}

export const actualContainers = (wemi: Wemi): ActualContainerItem[] | undefined | null => {
    if(wemi.item) {
        return itemSelectors.actualContainers(wemi.item)
    }

    return []
}

export const conditioningMaterial = (wemi: Wemi, locale: string): string => {
    if(wemi.item) {
        return itemSelectors.conditioningMaterial(wemi.item, locale)
    }

    return ''
}

export const ownerInstitutions = (wemi: Wemi): OwnerInstitutionResource[] | null | undefined => {
    if(wemi.item) {
        return itemSelectors.ownerInstitutions(wemi.item)
    }

    return []
}

// work fields
export const credits = (wemi: Wemi) => wemi.work ? workSelectors.credits(wemi.work) : null as any; // author name and function
export const workId = (wemi: Wemi) => wemi.work ? workSelectors.id(wemi.work) : "";
export const workType = (wemi: Wemi) => wemi.work ? workSelectors.workType(wemi.work) : "";
const workCreationDate = (wemi: Wemi) => wemi.work ? workSelectors.creationDate(wemi.work) : "";

export const creationDateSkinDate = (wemi: Wemi): SkinDate | undefined | null => {
    if(wemi.work) {
        return workSelectors.creationDateSkinDate(wemi.work)
    }

    return undefined
} 

export const descriptionMuseumWorkTitles = (wemi: Wemi, locale: string): string => {
    const title = get(wemi, 'work.descriptionMuseumWorkSchema.workTitles[0].title', undefined)
    if(title) {
        return getTranslatedString(
            title,
            locale,
        )
    }

    return ''
}

// manifestations fields
export const manifestationId = (wemi: Wemi) => wemi.manifestation ? manifestationSelectors.id(wemi.manifestation) : "";

export const domains = (wemi: Wemi): string[] | undefined | null => {
    if(wemi.manifestation) {
        return manifestationSelectors.domains(wemi.manifestation)
    }

    return []
}

export const domain = (wemi: Wemi) => wemi.manifestation ? manifestationSelectors.domain(wemi.manifestation) : "";

export const name = (wemi: Wemi) => wemi.manifestation ? manifestationSelectors.name(wemi.manifestation) : "";

export const denominations = (wemi: Wemi): DenominationResource[] | undefined | null => {
    if(wemi.manifestation) {
        return manifestationSelectors.denominations(wemi.manifestation)
    }

    return undefined
}

export const descriptions = (wemi: Wemi): TypedTextResource[] => {
    if(wemi.manifestation) {
        return manifestationSelectors.descriptions(wemi.manifestation)
    }

    return []
}

export const firstDescription = (wemi: Wemi): TypedTextResource | undefined => {
    if(wemi.manifestation) {
        return manifestationSelectors.firstDescription(wemi.manifestation)
    }

    return undefined
}

export const materialsAndTechnics = (wemi: Wemi) => wemi.manifestation ? manifestationSelectors.materialsAndTechnics(wemi.manifestation) : "";

export const materials = (wemi: Wemi): MaterialResource[] => {
    if(wemi.manifestation) {
        return manifestationSelectors.materials(wemi.manifestation)
    }

    return []
}

export const techniques = (wemi: Wemi): TechniqueResource[] => {
    if(wemi.manifestation) {
        return manifestationSelectors.techniques(wemi.manifestation)
    }

    return []
}

export const measurements = (wemi: Wemi): MeasurementResource[] => {
    if(wemi.manifestation) {
        return manifestationSelectors.measurements(wemi.manifestation)
    }
    return []
}


// expression fields
export const expressionId = (wemi: Wemi) => "" // unused for now but may be useful in the future

// other fields to complete
export const weight = (wemi: Wemi) => "";
